import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ServiceLevelsSchema, type ServiceLevels } from "../../shared/support-service-levels";
import {
  SUPPORT_TICKET_PRIORITIES,
  type SupportTicketPriority,
} from "../../shared/support-schemas";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { LabeledInput } from "./support-fields";
import { supportKeys, updateSupportSettings } from "./support-api";
import { PRIORITY_LABELS } from "./support-format";

// ADR-0089: how soon the team should reply, counted in the desk's business hours.

/** Monday first, the way people read a week; values are JavaScript's day numbers. */
const WEEK = [
  [1, "Mon"],
  [2, "Tue"],
  [3, "Wed"],
  [4, "Thu"],
  [5, "Fri"],
  [6, "Sat"],
  [0, "Sun"],
] as const;

type Draft = {
  targets: Record<SupportTicketPriority, string>;
  days: readonly number[];
  opens: string;
  closes: string;
  timeZone: string;
};

const clock = (minute: number) =>
  `${String(Math.floor(minute / 60) % 24).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;

function minuteOf(time: string, closing: boolean): number {
  const match = /^(\d{2}):(\d{2})$/u.exec(time);
  if (!match) return Number.NaN;
  const minute = Number(match[1]) * 60 + Number(match[2]);
  return closing && minute === 0 ? 1_440 : minute;
}

function toDraft(levels: ServiceLevels): Draft {
  return {
    targets: {
      urgent: String(levels.targets.urgent / 60),
      high: String(levels.targets.high / 60),
      normal: String(levels.targets.normal / 60),
      low: String(levels.targets.low / 60),
    },
    days: levels.days,
    opens: clock(levels.startMinute),
    closes: clock(levels.endMinute),
    timeZone: levels.timeZone,
  };
}

function fromDraft(draft: Draft) {
  const minutes = (hours: string) => Math.round(Number(hours) * 60);
  return ServiceLevelsSchema.safeParse({
    timeZone: draft.timeZone.trim(),
    days: draft.days,
    startMinute: minuteOf(draft.opens, false),
    endMinute: minuteOf(draft.closes, true),
    targets: {
      urgent: minutes(draft.targets.urgent),
      high: minutes(draft.targets.high),
      normal: minutes(draft.targets.normal),
      low: minutes(draft.targets.low),
    },
  });
}

function Targets({ draft, onChange }: { draft: Draft; onChange: (draft: Draft) => void }) {
  return (
    <fieldset className="grid gap-4 sm:grid-cols-4">
      <legend className="mb-2 font-semibold">Reply within (business hours)</legend>
      {SUPPORT_TICKET_PRIORITIES.map((priority) => (
        <LabeledInput
          key={priority}
          id={`support-target-${priority}`}
          label={PRIORITY_LABELS[priority]}
          value={draft.targets[priority]}
          disabled={false}
          onChange={(value) => {
            onChange({ ...draft, targets: { ...draft.targets, [priority]: value } });
          }}
        />
      ))}
    </fieldset>
  );
}

function Days({ draft, onChange }: { draft: Draft; onChange: (draft: Draft) => void }) {
  return (
    <fieldset className="flex flex-wrap gap-3">
      <legend className="mb-2 font-semibold">Business days</legend>
      {WEEK.map(([day, name]) => (
        <label key={day} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4 accent-[var(--primary)]"
            checked={draft.days.includes(day)}
            onChange={(event) => {
              const days = event.currentTarget.checked
                ? [...draft.days, day]
                : draft.days.filter((other) => other !== day);
              onChange({ ...draft, days });
            }}
          />
          {name}
        </label>
      ))}
    </fieldset>
  );
}

function Hours({ draft, onChange }: { draft: Draft; onChange: (draft: Draft) => void }) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <LabeledInput
        id="support-opens"
        label="Opens (24-hour time)"
        value={draft.opens}
        disabled={false}
        onChange={(opens) => {
          onChange({ ...draft, opens });
        }}
      />
      <LabeledInput
        id="support-closes"
        label="Closes (24-hour time)"
        value={draft.closes}
        disabled={false}
        onChange={(closes) => {
          onChange({ ...draft, closes });
        }}
      />
      <LabeledInput
        id="support-time-zone"
        label="Time zone"
        value={draft.timeZone}
        disabled={false}
        onChange={(timeZone) => {
          onChange({ ...draft, timeZone });
        }}
      />
    </div>
  );
}

/** The reply targets and business hours, saved together. */
export function SupportReplyTimesForm({ levels }: { levels: ServiceLevels }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(() => toDraft(levels));
  const parsed = fromDraft(draft);
  const save = useMutation({
    mutationFn: (serviceLevels: ServiceLevels) => updateSupportSettings({ serviceLevels }),
    onSuccess: (next) => {
      queryClient.setQueryData(supportKeys.settings(), next);
    },
  });
  const problem = save.error?.message ?? (parsed.success ? null : parsed.error.issues[0]?.message);
  return (
    <form
      aria-label="Reply times"
      className="flex flex-col gap-5 rounded-xl bg-card p-6 shadow-card"
      onSubmit={(event) => {
        event.preventDefault();
        if (parsed.success) save.mutate(parsed.data);
      }}
    >
      <Targets draft={draft} onChange={setDraft} />
      <Days draft={draft} onChange={setDraft} />
      <Hours draft={draft} onChange={setDraft} />
      {problem ? <FieldError>{problem}</FieldError> : null}
      <div>
        <Button
          type="submit"
          disabled={
            !parsed.success ||
            JSON.stringify(parsed.data) === JSON.stringify(levels) ||
            save.isPending
          }
        >
          {save.isPending ? "Saving…" : "Save reply times"}
        </Button>
      </div>
    </form>
  );
}
